import type { Product } from '#bv/data/products'
import { DisplayPrice } from '#bv/components/DisplayPrice'
import { StockAvailabilityBadge } from '#bv/components/StockAvailabilityBadge'
import { productCardBlurb, showSharedAccountCardDisclaimer } from '#bv/lib/productCardCopy'

export function ProductCardMetaRow({
  label,
  rating,
  tierCount,
  showRating = true,
}: {
  label: string
  rating: string
  tierCount?: number
  showRating?: boolean
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 gap-y-1">
      <span className="truncate text-[0.65rem] font-semibold uppercase tracking-[0.14em]" style={{ color: 'var(--text-faint)' }}>
        {label}
      </span>
      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
        {tierCount != null && tierCount > 1 ? (
          <span
            className="rounded-full px-2 py-0.5 text-[0.6rem] font-semibold"
            style={{
              background: 'linear-gradient(180deg, var(--surface-2), var(--surface-1))',
              border: '1px solid var(--border-soft)',
              color: 'var(--text-soft)',
            }}
          >
            {tierCount} plans
          </span>
        ) : null}
        {showRating ? (
          <span className="flex items-center gap-1 text-[0.68rem] font-semibold tabular-nums" style={{ color: 'var(--accent)' }}>
            <span className="text-[0.55rem]" style={{ color: 'var(--accent)' }} aria-hidden>
              ★
            </span>
            {rating}
          </span>
        ) : null}
      </div>
    </div>
  )
}

export function ProductCardBlurbBlock({ product }: { product: Product }) {
  const blurb = productCardBlurb(product)
  const showSharedAccount = showSharedAccountCardDisclaimer(product)
  if (!blurb && !showSharedAccount) return null

  return (
    <div className="mt-2 space-y-1.5 border-t border-white/[0.06] pt-2">
      {blurb ? (
        <p className="line-clamp-2 text-[0.72rem] leading-[1.45]" style={{ color: 'rgba(255,240,247,0.70)' }}>
          {blurb}
        </p>
      ) : null}
      {showSharedAccount ? (
        <p className="text-[0.62rem] leading-snug" style={{ color: 'rgba(255,240,247,0.46)' }}>
          Shared access — not guaranteed duration · no refund after delivery · invalid login on arrival: contact support
          promptly.
        </p>
      ) : null}
    </div>
  )
}

export function ProductCardPriceBlock({
  product,
  bundleMin,
  size = 'md',
}: {
  product: Product
  bundleMin?: number
  size?: 'sm' | 'md'
}) {
  const priceClass =
    size === 'sm'
      ? 'font-display text-lg tracking-wide'
      : 'font-display text-2xl tracking-wide'

  const fromLabel = (
    <span className="text-[0.62rem] font-semibold uppercase tracking-[0.12em]" style={{ color: 'var(--text-faint)' }}>
      From
    </span>
  )

  if (product.bundlePicker && bundleMin != null) {
    return (
      <div className="flex flex-col gap-0.5">
        {fromLabel}
        <DisplayPrice usd={bundleMin} className={priceClass} />
      </div>
    )
  }

  if (product.robuxConfig || product.serverBoostConfig) {
    return (
      <div className="flex flex-col gap-0.5">
        {fromLabel}
        <DisplayPrice usd={product.price} className={priceClass} />
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-baseline gap-2">
      {product.compareAtPrice != null && product.compareAtPrice > product.price ? (
        <DisplayPrice usd={product.compareAtPrice} className="text-sm tabular-nums line-through" style={{ color: 'var(--text-faint)' } as any} />
      ) : null}
      <DisplayPrice usd={product.price} className={priceClass} style={{ color: 'var(--text-strong)' } as any} />
    </div>
  )
}

export function ProductCardFooter({
  product,
  productInStock,
  bundleMin,
  ctaLabel,
  size = 'md',
}: {
  product: Product
  productInStock: boolean
  bundleMin?: number
  ctaLabel: string
  size?: 'sm' | 'md'
}) {
  return (
    <div
      className="mt-auto rounded-xl p-3 pt-2.5"
      style={{
        background: 'linear-gradient(180deg, var(--surface-2) 0%, rgba(0,0,0,0.18) 100%)',
        border: '1px solid var(--border-soft)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
      }}
    >
      <StockAvailabilityBadge inStock={productInStock} size={size === 'sm' ? 'compact' : 'comfortable'} />
      <div className="mt-2.5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-3">
        <ProductCardPriceBlock product={product} bundleMin={bundleMin} size={size} />
        <span
          className={`inline-flex w-full items-center justify-center rounded-full font-semibold sm:w-auto ${
            size === 'sm' ? 'min-h-[2rem] px-3 text-[0.65rem]' : 'min-h-[2.25rem] px-3.5 text-xs'
          } ${
            productInStock
              ? 'btn-primary'
              : 'border border-[var(--border-soft)] bg-gradient-to-b from-[var(--surface-2)] to-[var(--surface-1)] text-[var(--text-faint)]'
          }`}
        >
          {productInStock ? ctaLabel : 'Unavailable'}
        </span>
      </div>
    </div>
  )
}
